import { Injectable } from '@angular/core';
import { Role } from '../../model/enum/role';
import { PaymentMethod } from '../../model/enum/payment-method';
import { ApplicableDiscount } from '../../model/discount-applicable';
import { CreateTransactionDtoStatus } from '../../services/transactions/types/create-transaction-dto-status';
import { FindCustomerStatutoryDiscountsResponseDto } from '../../services/customers/dtos/find-customer-statutory-discounts.response.dto';
import { FindCustomerNamesResponseDto } from '../../services/customers/dtos/find-customer-names.response.dto';
import { TransactionServiceType } from '../../services/transactions/enums/transaction-service-type.enum';
import { CurrentTransactionSerializedItemEntry } from '../../services/shared/current-transaction/types/current-transaction';

export enum LocalStorageKey {
  // Auth-related
  ACCESS_TOKEN = 'auth.accessToken',
  REFRESH_TOKEN = 'auth.refreshToken',
  PENDING_VERIFICATION_EMAIL = 'auth.pendingVerificationEmail',

  // POS Transaction
  POS_ITEMS = 'pos.items',
  POS_TRANSACTION_STATUS = 'pos.transactionStatus',
  POS_DISCOUNT = 'pos.discountId',
  POS_PAYMENT_METHOD = 'pos.paymentMethod',
  POS_CUSTOMER = 'pos.customer',
  POS_TRANSACTION_REFERENCE = 'pos.transactionReference',
  POS_TRANSACTION_SERVICE_TYPE = 'pos.transactionServiceType',
  POS_CUSTOMER_STATUTORY_DISCOUNTS = 'pos.customerStatutoryDiscounts',
  POS_INCLUDE_BUYER_INFO = 'pos.includeBuyerInfo',
  POS_BNPC_REMAINING_THIS_WEEK = 'pos.bnpcRemainingThisWeek',
  POS_BNPC_ITEMS_COUNT_THIS_WEEK = 'pos.bnpcItemsCountThisWeek',
  POS_TRANSACTION_UUID = 'pos.transactionUuid',

  // Barcode Generator Context
  BARCODE_GENERATOR_STORE_ID = 'barcode-generator.context.storeId',
}

export const AUTH_LOCAL_STORAGE_KEYS = [
  LocalStorageKey.ACCESS_TOKEN,
  LocalStorageKey.REFRESH_TOKEN,
  LocalStorageKey.PENDING_VERIFICATION_EMAIL,
];

export const POS_LOCAL_STORAGE_KEYS = [
  LocalStorageKey.POS_ITEMS,
  LocalStorageKey.POS_DISCOUNT,
  LocalStorageKey.POS_PAYMENT_METHOD,
  LocalStorageKey.POS_CUSTOMER,
  LocalStorageKey.POS_CUSTOMER_STATUTORY_DISCOUNTS,
  LocalStorageKey.POS_BNPC_REMAINING_THIS_WEEK,
  LocalStorageKey.POS_BNPC_ITEMS_COUNT_THIS_WEEK,
  LocalStorageKey.POS_INCLUDE_BUYER_INFO,
  LocalStorageKey.POS_TRANSACTION_STATUS,
  LocalStorageKey.POS_TRANSACTION_REFERENCE,
  LocalStorageKey.POS_TRANSACTION_SERVICE_TYPE,
  LocalStorageKey.POS_TRANSACTION_UUID,
];

// Keys that should be cleared from local storage upon logout.
// POS context keys are intentionally excluded to keep the previously selected
// store and register pre-filled when reopening the POS.
export const APP_LOCAL_STORAGE_KEYS = [
  ...AUTH_LOCAL_STORAGE_KEYS,
  ...POS_LOCAL_STORAGE_KEYS,
];

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  // TODO: Move tokens to HTTP-only cookies

  // Access token
  getAccessToken(): string | null {
    return this.getItem(LocalStorageKey.ACCESS_TOKEN);
  }

  setAccessToken(token: string): void {
    this.setItem(LocalStorageKey.ACCESS_TOKEN, token);
  }

  // Refresh token
  getRefreshToken(): string | null {
    return this.getItem(LocalStorageKey.REFRESH_TOKEN);
  }

  setRefreshToken(token: string): void {
    this.setItem(LocalStorageKey.REFRESH_TOKEN, token);
  }

  getPendingVerificationEmail(): string | null {
    return this.getItem<string>(LocalStorageKey.PENDING_VERIFICATION_EMAIL);
  }

  setPendingVerificationEmail(email: string | null): void {
    this.setItem(LocalStorageKey.PENDING_VERIFICATION_EMAIL, email);
  }

  // Reference
  getPosTransactionReference(): string | null {
    return this.getItem<string>(LocalStorageKey.POS_TRANSACTION_REFERENCE);
  }

  setPosTransactionReference(reference: string | null): void {
    this.setItem(LocalStorageKey.POS_TRANSACTION_REFERENCE, reference);
  }

  // Service Type
  getPosTransactionServiceType(): TransactionServiceType | null {
    return this.getItem<TransactionServiceType>(LocalStorageKey.POS_TRANSACTION_SERVICE_TYPE);
  }

  setPosTransactionServiceType(reference: TransactionServiceType | null): void {
    this.setItem(LocalStorageKey.POS_TRANSACTION_SERVICE_TYPE, reference);
  }

  // Customer
  getPosCustomer(): FindCustomerNamesResponseDto['data'][0] | null {
    return this.getItem<FindCustomerNamesResponseDto['data'][0]>(
      LocalStorageKey.POS_CUSTOMER,
    );
  }

  setPosCustomer(
    customer: FindCustomerNamesResponseDto['data'][0] | null,
  ): void {
    this.setItem(LocalStorageKey.POS_CUSTOMER, customer);
  }

  // Customer Statutory Discounts
  getPosCustomerStatutoryDiscounts(): FindCustomerStatutoryDiscountsResponseDto {
    return (
      this.getItem<FindCustomerStatutoryDiscountsResponseDto>(
        LocalStorageKey.POS_CUSTOMER_STATUTORY_DISCOUNTS,
      ) ?? []
    );
  }

  setPosCustomerStatutoryDiscounts(
    discounts: FindCustomerStatutoryDiscountsResponseDto,
  ): void {
    this.setItem(LocalStorageKey.POS_CUSTOMER_STATUTORY_DISCOUNTS, discounts);
  }

  // Include Buyer Info
  getPosIncludeBuyerInfo(): boolean {
    return this.getItem(LocalStorageKey.POS_INCLUDE_BUYER_INFO) === true;
  }

  setPosIncludeBuyerInfo(value: boolean): void {
    this.setItem(
      LocalStorageKey.POS_INCLUDE_BUYER_INFO,
      value ? 'true' : 'false',
    );
  }

  // Remaining BNPC Balance This Week
  getPosBnpcRemainingThisWeek(): number | null {
    return this.getItem<number>(LocalStorageKey.POS_BNPC_REMAINING_THIS_WEEK);
  }

  setPosBnpcRemainingThisWeek(value: number | null): void {
    this.setItem(LocalStorageKey.POS_BNPC_REMAINING_THIS_WEEK, value);
  }

  // BNPC Items This Week
  getPosBnpcItemsThisWeek(): number | null {
    return this.getItem<number>(LocalStorageKey.POS_BNPC_ITEMS_COUNT_THIS_WEEK);
  }

  setPosBnpcItemsCountThisWeek(value: number | null): void {
    this.setItem(LocalStorageKey.POS_BNPC_ITEMS_COUNT_THIS_WEEK, value);
  }

  // Transaction items
  getPosItems(): CurrentTransactionSerializedItemEntry[] | null {
    return this.getItem<CurrentTransactionSerializedItemEntry[]>(LocalStorageKey.POS_ITEMS);
  }

  setPosItems(items: CurrentTransactionSerializedItemEntry[]): void {
    this.setItem(LocalStorageKey.POS_ITEMS, items);
  }

  // Barcode Generator Store ID
  getBarcodeGeneratorStoreId(): number | null {
    return this.getItem<number>(LocalStorageKey.BARCODE_GENERATOR_STORE_ID);
  }

  setBarcodeGeneratorStoreId(storeId: number | null): void {
    this.setItem(LocalStorageKey.BARCODE_GENERATOR_STORE_ID, storeId);
  }

  // Discount
  getPosDiscount(): ApplicableDiscount | null {
    return this.getItem<ApplicableDiscount>(LocalStorageKey.POS_DISCOUNT);
  }

  setPosDiscount(discount: ApplicableDiscount | null): void {
    this.setItem(LocalStorageKey.POS_DISCOUNT, discount);
  }

  // Payment method
  getPosPaymentMethod(): PaymentMethod | null {
    return this.getItem<PaymentMethod>(LocalStorageKey.POS_PAYMENT_METHOD);
  }

  setPosPaymentMethod(paymentMethod: PaymentMethod): void {
    this.setItem(LocalStorageKey.POS_PAYMENT_METHOD, paymentMethod);
  }

  // Transaction ID
  getPosTransactionUuid(): string | null {
    return this.getItem<string>(LocalStorageKey.POS_TRANSACTION_UUID);
  }

  setPosTransactionUuid(transactionUuid: string): void {
    this.setItem(LocalStorageKey.POS_TRANSACTION_UUID, transactionUuid);
  }

  // Status
  getPosTransactionStatus(): CreateTransactionDtoStatus | null {
    return this.getItem<CreateTransactionDtoStatus>(
      LocalStorageKey.POS_TRANSACTION_STATUS,
    );
  }

  setPosTransactionStatus(status: CreateTransactionDtoStatus): void {
    this.setItem(LocalStorageKey.POS_TRANSACTION_STATUS, status);
  }

  // JWT-derived values
  getRole(): Role | null {
    return this.getDecodedField('role');
  }

  getIsOnboarded(): boolean | null {
    return this.getDecodedField('isOnboarded');
  }

  getUserId(): number | null {
    return this.getDecodedField('userId');
  }

  clearAppStorage(): void {
    for (const key of APP_LOCAL_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  }

  clearPosState(): void {
    for (const key of POS_LOCAL_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
  }

  // Generic get/set
  getItem<T = any>(key: string): T | null {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      // If not JSON, return raw string (for backward compatibility)
      return raw as unknown as T;
    }
  }

  setItem(key: string, value: unknown): void {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      localStorage.removeItem(key);
      return;
    }
    const serialized =
      typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);
  }

  private getDecodedField<T = any>(field: string): T | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const decoded = this.parseJwt(token);
    return decoded?.[field] ?? null;
  }

  // TODO: use jwt decode
  private parseJwt(token: string): any {
    try {
      const payloadBase64 = token.split('.')[1];
      const decodedPayload = atob(payloadBase64);
      return JSON.parse(decodedPayload);
    } catch {
      return null;
    }
  }
}
